import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { FormRoot, FormInput, FormNumber, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";



import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, SettingsIcon } from "lucide-react";
import { z } from "zod";
import { useCreateModifier } from "../api/queries";

const GeneralSchema = z
  .object({
    name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ຊຸດຕົວເລືອກ"),
    minSelect: z.number().int().min(0),
    maxSelect: z.number().int().min(0),
    isRequired: z.boolean(),
  })
  .refine((d) => d.minSelect <= d.maxSelect, {
    message: "Min Select ບໍ່ສາມາດໃຫຍ່ກວ່າ Max Select",
    path: ["maxSelect"],
  });

type GeneralValues = z.infer<typeof GeneralSchema>;

export function ModifierCreatePage() {
  const nav = useNavigate({ from: "/app/catalog/modifiers/create" });
  const createModifier = useCreateModifier();

  const generalForm = RHF.useForm<GeneralValues>({
    resolver: zodResolver(GeneralSchema),
    defaultValues: {
      name: "",
      minSelect: 0,
      maxSelect: 1,
      isRequired: false,
    },
  });

  const onSave = generalForm.handleSubmit(async (values) => {
    try {
      const row = await createModifier.mutateAsync(values);
      toast.success("ສ້າງຊຸດຕົວເລືອກສຳເລັດ");
      nav({
        to: "/app/catalog/modifiers/$id/edit",
        params: { id: row.id },
      });
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? "ສ້າງຊຸດຕົວເລືອກລົ້ມເຫຼວ");
    }
  });

  const minMaxError = generalForm.formState.errors.maxSelect?.message;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຊຸດຕົວເລືອກ",
              to: "/app/catalog/modifiers",
            },
            {
              label: "ສ້າງຊຸດຕົວເລືອກ",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ສ້າງຊຸດຕົວເລືອກ</h2>
            <p className="text-muted-foreground">ແຜງຄວບຄຸມ / ຊຸດຕົວເລືອກ</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/catalog/modifiers" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/catalog/modifiers" })}
            >
              ຍົກເລີກ
            </Button>
            <Button onClick={onSave} isLoading={createModifier.isPending}>
              ບັນທຶກການປ່ຽນແປງ
            </Button>
          </div>
        </div>

        <FormRoot<GeneralValues>
          methods={generalForm}
          onSubmit={onSave}
          className="space-y-6"
        >
          <Card className="rounded-xl border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <SettingsIcon className="size-5 text-muted-foreground" />
                ຂໍ້ມູນທົ່ວໄປ
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                ຕັ້ງຄ່ານະໂຍບາຍການ ແລະ ຕົວເລືອກປັບແຕ່ງສຳລັບລູກຄ້າ.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormInput
                name="name"
                label="ຊື່ຊຸດຕົວເລືອກ"
                placeholder="ຕົວຢ່າງ: Coffee Customization"
                requiredMark
              />
              <div className="flex items-center justify-between rounded-[12px] border bg-muted/30 p-4">
                <div>
                  <p className="font-medium text-sm">ບັງຄັບເລືອກ</p>
                  <p className="text-muted-foreground text-xs">
                    ລູກຄ້າຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ຕົວເລືອກ
                  </p>
                </div>
                <RHF.Controller
                  name="isRequired"
                  control={generalForm.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormNumber name="minSelect" label="ເລືອກຕໍ່າສຸດ" requiredMark />
                <FormNumber name="maxSelect" label="ເລືອກສູງສຸດ" requiredMark />
              </div>
              {minMaxError && (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-destructive text-sm">
                  {minMaxError}
                </div>
              )}
            </CardContent>
          </Card>
        </FormRoot>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t pt-6">
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/catalog/modifiers" })}
          >
            ຍົກເລີກການປ່ຽນແປງ
          </Button>
          <Button onClick={onSave} isLoading={createModifier.isPending}>
            ສ້າງຊຸດຕົວເລືອກ
          </Button>
        </div>
        <p className="mt-4 text-center text-muted-foreground text-sm">
          ຊຸດຕົວເລືອກກຳນົດກົດການປັບແຕ່ງສິນຄ້າ. ຫຼັງສ້າງແລ້ວສາມາດເພີ່ມຕົວເລືອກໄດ້.
        </p>
      </Main>
    </>
  );
}
